import { Box, Button, Pagination, Typography } from '@mui/material';
import { ArrowLeft, ArrowRight } from 'phosphor-react';
import { Meta } from '../../Utilis/Types/Generic';

type TableListProps = {
  columns: {
    name?: string;
    field: string;
    render?: (data: any) => JSX.Element;
  }[];
  data: any[];
  onPageChange: (page: number) => void;
  meta?: Meta;
};

export const TableList = ({
  columns,
  data,
  meta = {
    page: 1,
    pageCount: 1,
    total: 1,
    hasNextPage: false,
    hasPreviousPage: false,
    pageSize: 1,
    itemCount: 1,
  },
  onPageChange,
}: TableListProps) => {
  const Header = (
    <Box
      sx={{
        display: 'table-row',
        position: 'sticky',
        top: 0,
        borderBottom: '1px solid #EAECF0',
        backgroundColor: 'white',
        zIndex: 1,
      }}
    >
      {columns.map(column => (
        <Box
          sx={{
            display: 'table-cell',
            padding: '13px 24px',
            width: !!column.name ? 'auto' : 0,
          }}
        >
          <Typography
            sx={{ fontSize: '12px', color: '#202A2D', fontWeight: 'bold' }}
          >
            {column.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const Body = data.map((data: any) => (
    <Box
      sx={{
        display: 'table-row',
        borderBottom: '1px solid #EAECF0',
      }}
    >
      {columns.map(column => {
        const content = !!column.render ? (
          column.render(data?.[column.field] || data)
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              padding: '26px 24px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{ fontSize: '14px', color: '#101828', fontWeight: 'bold' }}
            >
              {data?.[column.field] || '-'}
            </Typography>
          </Box>
        );

        return (
          <Box
            sx={{
              display: 'table-cell',
              verticalAlign: 'middle',
            }}
          >
            {content}
          </Box>
        );
      })}
    </Box>
  ));

  const PaginationSection = (
    <Box
      sx={{
        padding: '14px 24px 18px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid #EAECF0',
      }}
    >
      <Button
        variant='outlined'
        sx={{
          display: 'flex',
          gap: '8px',
          textTransform: 'none',
          borderRadius: '8px',
          border: '1px solid #D0D5DD',
          fontSize: '12px',
          color: '#344054',
          padding: '8px 14px',
          fontWeight: 'bold',
        }}
        disabled={!meta?.hasPreviousPage}
        onClick={() => onPageChange(meta.page - 1)}
      >
        <ArrowLeft size={17} />
        Previous
      </Button>

      <Pagination
        count={meta.pageCount}
        defaultPage={1}
        page={meta.page}
        siblingCount={1}
        boundaryCount={2}
        hidePrevButton
        hideNextButton
        onChange={(e: any, value: number) => onPageChange(value)}
      />

      <Button
        variant='outlined'
        sx={{
          display: 'flex',
          gap: '8px',
          textTransform: 'none',
          borderRadius: '8px',
          border: '1px solid #D0D5DD',
          fontSize: '12px',
          color: '#344054',
          padding: '8px 14px',
          fontWeight: 'bold',
        }}
        disabled={!meta?.hasNextPage}
        onClick={() => onPageChange(meta.page + 1)}
      >
        Next
        <ArrowRight size={17} />
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '41px',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      <Box
        sx={{
          overflowY: 'scroll',
          '::-webkit-scrollbar': { display: 'none' },
          borderBottom: '1px solid #EAECF0',
          background: 'white',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'table',
            backgroundColor: 'white',
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          {Header}
          {Body}
        </Box>
      </Box>

      {PaginationSection}
    </Box>
  );
};
